import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  User, ChevronDown, ChevronUp, AlertTriangle, Phone, Mail,
  CheckCircle, FileCheck, Droplets, PenTool, Building2, UserCheck
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import EPMSPConsultation from '../EPMSPConsultation';
import { getAuthPhotoUrl } from '../AuthImage';
import CompetencesPrerequisConsultation from '../CompetencesPrerequisConsultation';

const COLORS = [
  { border: 'border-l-[#8DC63F]', bg: 'bg-[#f7fdf0]', badge: 'bg-[#8DC63F]' },
  { border: 'border-l-[#3B82F6]', bg: 'bg-[#eff6ff]', badge: 'bg-[#3B82F6]' },
  { border: 'border-l-[#F59E0B]', bg: 'bg-[#fffbeb]', badge: 'bg-[#F59E0B]' },
  { border: 'border-l-[#EC4899]', bg: 'bg-[#fdf2f8]', badge: 'bg-[#EC4899]' },
  { border: 'border-l-[#8B5CF6]', bg: 'bg-[#f5f3ff]', badge: 'bg-[#8B5CF6]' },
];

const CONTRACT_LABELS = {
  stagiaire_fp: 'Stagiaire formation pro.',
  apprenti: 'Apprenti',
  contrat_pro: 'Contrat pro.',
  salarie: 'Salarié',
};

const StagiaireAccordionCardFormateur = ({ stagiaire, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('profil');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  const color = COLORS[index % COLORS.length];
  const sId = stagiaire.user_id;
  const initials = `${(stagiaire.user_info?.prenom || stagiaire.prenom || '')[0] || ''}${(stagiaire.user_info?.nom || stagiaire.nom || '')[0] || ''}`.toUpperCase();
  const stagiaireNom = `${stagiaire.user_info?.prenom || stagiaire.prenom || ''} ${stagiaire.user_info?.nom || stagiaire.nom || ''}`;
  const typeContrat = stagiaire.type_contrat;
  const tuteurNom = stagiaire.tuteur_info
    ? `${stagiaire.tuteur_info.prenom || ''} ${stagiaire.tuteur_info.nom || ''}`.trim()
    : stagiaire.tuteur_nom || '';
  const entrepriseNom = stagiaire.entreprise_info
    ? (stagiaire.entreprise_info.entreprise_nom || `${stagiaire.entreprise_info.prenom || ''} ${stagiaire.entreprise_info.nom || ''}`.trim())
    : stagiaire.entreprise_nom || '';

  useEffect(() => {
    if (isExpanded && !profile && !loading) loadProfile();
  }, [isExpanded]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/stagiaires/${sId}`);
      setProfile(res.data);
    } catch (e) {
    }
    setLoading(false);
  };

  return (
    <div
      className={`${color.bg} border border-slate-200 ${color.border} border-l-4 rounded-lg shadow-sm transition-all hover:shadow-md`}
      data-testid={`stagiaire-card-${sId}`}
    >
      {/* Header */}
      <div
        className="p-4 cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid={`stagiaire-header-${sId}`}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 min-w-0">
            {(stagiaire.user_info?.photo_identite || stagiaire.user_info?.photo_url || stagiaire.photo_identite) ? (
              <img src={getAuthPhotoUrl(stagiaire.user_info?.photo_identite || stagiaire.user_info?.photo_url || stagiaire.photo_identite)}
                alt={stagiaireNom} className="w-11 h-11 rounded-full object-cover shadow-sm flex-shrink-0 border-2 border-white" data-testid={`stagiaire-photo-${sId}`} />
            ) : (
              <div className={`w-11 h-11 ${color.badge} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0`}>
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-[#0F172A] text-base">{stagiaireNom}</p>
                {typeContrat && (
                  <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-[10px] px-1.5 py-0">
                    {CONTRACT_LABELS[typeContrat] || typeContrat}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-500">
                {stagiaire.centre_formation || stagiaire.centre || ''} — {stagiaire.statut || stagiaire.statut_formation || ''}
              </p>
              <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-slate-500">
                {tuteurNom && (
                  <span className="flex items-center gap-1">
                    <UserCheck className="h-3 w-3 text-amber-500" />
                    Tuteur : <span className="font-medium text-slate-700">{tuteurNom}</span>
                  </span>
                )}
                {entrepriseNom && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3 w-3 text-emerald-500" />
                    Entreprise : <span className="font-medium text-slate-700">{entrepriseNom}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <Link
              to={`/notation?stagiaire=${sId}`}
              className="px-3 py-1.5 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors flex items-center gap-1"
              data-testid={`btn-noter-${sId}`}
            >
              <PenTool className="h-4 w-4" /> Noter
            </Link>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-lg hover:bg-white/60 transition-colors"
              data-testid={`btn-expand-${sId}`}
            >
              {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-200/60">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]" />
              <span className="ml-3 text-sm text-slate-500">Chargement...</span>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-3">
              <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-slate-100/80 p-1 rounded-lg">
                <TabsTrigger value="profil" className="flex-1 min-w-[80px] text-xs sm:text-sm gap-1" data-testid={`tab-profil-${sId}`}>
                  <User className="h-3.5 w-3.5" /> Profil
                </TabsTrigger>
                <TabsTrigger value="evaluations" className="flex-1 min-w-[80px] text-xs sm:text-sm gap-1" data-testid={`tab-evaluations-${sId}`}>
                  <FileCheck className="h-3.5 w-3.5" /> EPMSP
                </TabsTrigger>
                <TabsTrigger value="competences" className="flex-1 min-w-[80px] text-xs sm:text-sm gap-1" data-testid={`tab-competences-${sId}`}>
                  <CheckCircle className="h-3.5 w-3.5" /> Compétences
                </TabsTrigger>
              </TabsList>

              {/* TAB: Profil */}
              <TabsContent value="profil" className="mt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-500" /> Informations de contact
                    </h4>
                    <div className="bg-white rounded-lg p-4 space-y-2 text-sm border border-slate-100">
                      <p className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-slate-500 w-24 inline-block">Email :</span>
                        <span className="font-medium">{profile?.user_info?.email || profile?.email || stagiaire.email || '-'}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-slate-500 w-24 inline-block">Téléphone :</span>
                        <span className="font-medium">{profile?.user_info?.telephone || profile?.telephone || stagiaire.telephone || '-'}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <Droplets className="h-3.5 w-3.5 text-red-400" />
                        <span className="text-slate-500 w-24 inline-block">Gr. sanguin :</span>
                        <span className="font-medium">{profile?.groupe_sanguin || '-'}</span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-500" /> Contact d'urgence
                    </h4>
                    <div className="bg-red-50 rounded-lg border border-red-200 p-4 space-y-2 text-sm">
                      <p>
                        <span className="text-slate-500 w-24 inline-block">Nom :</span>
                        <span className="font-medium">{profile?.contact_urgence_nom || '-'}</span>
                      </p>
                      <p>
                        <span className="text-slate-500 w-24 inline-block">Prénom :</span>
                        <span className="font-medium">{profile?.contact_urgence_prenom || '-'}</span>
                      </p>
                      <p>
                        <span className="text-slate-500 w-24 inline-block">Tél :</span>
                        <span className="font-medium">{profile?.contact_urgence_telephone || '-'}</span>
                      </p>
                      <p>
                        <span className="text-slate-500 w-24 inline-block">Lien :</span>
                        <span className="font-medium">{profile?.contact_urgence_filiation || '-'}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* TAB: EPMSP */}
              <TabsContent value="evaluations" className="mt-3">
                <EPMSPConsultation stagiaireId={sId} stagiaireNom={stagiaireNom} />
              </TabsContent>

              {/* TAB: Compétences */}
              <TabsContent value="competences" className="mt-3">
                <CompetencesPrerequisConsultation stagiaireId={sId} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      )}
    </div>
  );
};

export default StagiaireAccordionCardFormateur;
